import { messagesSignal } from '../state/messages.store';
import { activeContactSignal } from '../state/contacts.store';
import { showToast } from '../state/ui.store';
import type { Message } from '../types/message';

/**
 * Helper to check if a selection range intersects a DOM element.
 */
function rangeIntersectsElement(range: Range, el: HTMLElement): boolean {
  try {
    const elRange = document.createRange();
    elRange.selectNodeContents(el);
    return (
      range.compareBoundaryPoints(Range.END_TO_START, elRange) < 0 &&
      range.compareBoundaryPoints(Range.START_TO_END, elRange) > 0
    );
  } catch {
    return false;
  }
}

/**
 * Format timestamp into pt-BR date/time string [DD/MM/YYYY HH:mm]
 */
function formatTimestamp(timestampStr: string | number | Date): string {
  const d = new Date(timestampStr);
  if (isNaN(d.getTime())) return '';
  const dateStr = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `[${dateStr} ${timeStr}]`;
}

/**
 * Clean up formatted message content (remove raw bracket names, format quotes and mentions)
 */
function cleanMessageContent(rawContent: string, message?: Message): string {
  let content = rawContent || '';

  // 1. Remove bracket sender prefix [Name]: if present
  const bracketMatch = content.match(/^\[([\s\S]*?)\]:\s*([\s\S]*)$/);
  if (bracketMatch) {
    content = bracketMatch[2].trim();
  }

  // 2. Clean up quoted message format: > [[@Author]]: snippet\n\nMainBody
  const quoteMatch = content.match(/^>\s*\[\[@([\s\S]+?)\]\]:\s*([\s\S]+?)(?:\n\n|\n(?=[^\n]))([\s\S]*)$/);
  if (quoteMatch) {
    const author = quoteMatch[1].trim();
    const quoteSnippet = quoteMatch[2].trim();
    const mainBody = quoteMatch[3].trim();
    content = `> [${author}]: "${quoteSnippet}"\n${mainBody}`;
  }

  // 3. Normalize mention tags [[@Name]] -> @Name and [[@todos]] -> @Todos
  content = content.replace(/\[\[@([\s\S]+?)\]\]/g, '@$1');
  content = content.replace(/@all|@todos/gi, '@Todos');

  // 4. Append transcription if message is audio/image/document with extracted_text
  if (message && message.extracted_text) {
    const transcript = message.extracted_text.trim();
    if (transcript) {
      if (message.message_type === 'audio') {
        content = content ? `${content}\n🎤 [Áudio Transcrito]: "${transcript}"` : `🎤 [Áudio Transcrito]: "${transcript}"`;
      } else if (message.message_type === 'image' || message.message_type === 'sticker') {
        content = content ? `${content}\n🖼️ [Análise Visual/OCR]: "${transcript}"` : `🖼️ [Análise Visual/OCR]: "${transcript}"`;
      } else if (message.message_type === 'document') {
        content = content ? `${content}\n📄 [Documento]: "${transcript}"` : `📄 [Documento]: "${transcript}"`;
      }
    }
  }

  return content.trim();
}

/**
 * Formats a single WhatsApp message object into a clean structured string:
 * [25/07/2026 18:36] Remetente:
 * Conteúdo limpo
 */
function formatSingleMessageStructured(el: HTMLElement, msg?: Message): string {
  const activeContact = activeContactSignal.value;
  let senderName = '';
  let timestampFormatted = '';
  let content = '';

  if (msg) {
    const isUser = msg.sender_type === 'user';
    senderName = isUser ? 'Você' : (msg.sender_name || activeContact?.name || 'Contato');
    timestampFormatted = formatTimestamp(msg.timestamp);
    content = cleanMessageContent(msg.content || '', msg);
  } else {
    // Fallback: parse attributes from DOM element
    const senderType = el.getAttribute('data-sender-type');
    const rawSenderName = el.getAttribute('data-sender-name');
    const rawTimestamp = el.getAttribute('data-timestamp');

    senderName = senderType === 'user' ? 'Você' : (rawSenderName || activeContact?.name || 'Contato');
    timestampFormatted = rawTimestamp ? formatTimestamp(rawTimestamp) : '';

    const pEl = el.querySelector('.message-bubble p') || el.querySelector('.message-bubble');
    content = pEl ? cleanMessageContent(pEl.textContent || '') : '';
  }

  // Strip UI ticks and reaction numbers if leftover
  content = content.replace(/\s*✓+$/, '').trim();

  if (!content) return '';

  const header = timestampFormatted ? `${timestampFormatted} ${senderName}:` : `${senderName}:`;
  return `${header}\n${content}`;
}

/**
 * Handle copy event for chat messages and Alfred AI drawer
 */
function handleCopyEvent(e: ClipboardEvent) {
  // 1. Ignore if copy occurred inside an editable element
  const activeEl = document.activeElement as HTMLElement | null;
  if (
    activeEl &&
    (activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      activeEl.isContentEditable)
  ) {
    return;
  }

  // 2. Get DOM selection
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return;
  }

  const range = selection.getRangeAt(0);

  // 3. Find intersecting message bubbles and Alfred drawer elements
  const allMessageEls = Array.from(document.querySelectorAll<HTMLElement>('[data-message-id]'));
  const selectedMessageEls = allMessageEls.filter(el => rangeIntersectsElement(range, el));

  const allAiEls = Array.from(document.querySelectorAll<HTMLElement>('[data-ai-message="true"]'));
  const selectedAiEls = allAiEls.filter(el => rangeIntersectsElement(range, el));

  if (selectedMessageEls.length === 0 && selectedAiEls.length === 0) {
    return; // Let standard copy handle other text on page
  }

  let formattedOutput = '';

  // Case A: Partial selection inside a single message content paragraph without selecting headers/timestamps/reactions
  if (selectedMessageEls.length === 1 && selectedAiEls.length === 0) {
    const el = selectedMessageEls[0];
    const bubbleContent = el.querySelector('.message-bubble p') || el.querySelector('.message-bubble');

    // Check if selection start & end are entirely inside the bubble content node
    if (bubbleContent && bubbleContent.contains(range.startContainer) && bubbleContent.contains(range.endContainer)) {
      const selectedRawText = selection.toString().trim();
      if (selectedRawText) {
        const cleanedText = cleanMessageContent(selectedRawText);
        if (cleanedText && e.clipboardData) {
          e.preventDefault();
          e.clipboardData.setData('text/plain', cleanedText);
          showToast('Texto copiado ✨', 'info');
          return;
        }
      }
    }
  }

  // Case B: Selection touches 1 or more full message bubbles or multiple messages
  const messageMap = new Map<string, Message>();
  (messagesSignal.value || []).forEach(m => messageMap.set(m.message_id, m));

  const blocks: string[] = [];

  // Format Chat Messages
  selectedMessageEls.forEach(el => {
    const msgId = el.getAttribute('data-message-id');
    const msg = msgId ? messageMap.get(msgId) : undefined;
    const formatted = formatSingleMessageStructured(el, msg);
    if (formatted) {
      blocks.push(formatted);
    }
  });

  // Format Alfred AI Messages
  selectedAiEls.forEach(el => {
    const role = el.getAttribute('data-ai-role');
    const sender = role === 'user' ? 'Você' : 'Alfred AI';
    const contentEl = el.querySelector('.markdown-body') || el;
    const rawText = contentEl ? contentEl.textContent || '' : '';
    const cleanText = rawText.trim();
    if (cleanText) {
      blocks.push(`[${sender}]:\n${cleanText}`);
    }
  });

  formattedOutput = blocks.join('\n\n');

  if (formattedOutput && formattedOutput.trim().length > 0 && e.clipboardData) {
    e.preventDefault();

    // Set plain text
    e.clipboardData.setData('text/plain', formattedOutput);

    // Set HTML text for rich clipboard apps (e.g. Word, Notion, Docs)
    const htmlLines = formattedOutput
      .split('\n')
      .map(line => {
        if (!line.trim()) return '<br/>';
        if (/^\[.*?\]/.test(line)) {
          return `<p style="font-weight: bold; color: #0891b2; margin-top: 8px; margin-bottom: 2px;">${escapeHtml(line)}</p>`;
        }
        return `<p style="margin: 0; line-height: 1.5;">${escapeHtml(line)}</p>`;
      })
      .join('');

    e.clipboardData.setData('text/html', `<div style="font-family: sans-serif; font-size: 14px;">${htmlLines}</div>`);

    showToast('Copiado no formato estruturado ✨', 'info');
  }
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

let isInitialized = false;

/**
 * Initialize structured copy listener globally
 */
export function initStructuredCopyService() {
  if (isInitialized) return;
  isInitialized = true;
  document.addEventListener('copy', handleCopyEvent);
}
